import { NO_PRICE } from '../pricelist/quickprice';

// Mapping from ISO 3166-1 alpha-2 country codes to IANA timezone identifiers
// Uses the most common/capital timezone for each country
const countryToTimezone: Record<string, string> = {
  AT: 'Europe/Vienna',
  DE: 'Europe/Berlin',
  CH: 'Europe/Zurich',
  IT: 'Europe/Rome',
  FR: 'Europe/Paris',
  ES: 'Europe/Madrid',
  PT: 'Europe/Lisbon',
  NL: 'Europe/Amsterdam',
  BE: 'Europe/Brussels',
  LU: 'Europe/Luxembourg',
  GB: 'Europe/London',
  IE: 'Europe/Dublin',
  DK: 'Europe/Copenhagen',
  SE: 'Europe/Stockholm',
  NO: 'Europe/Oslo',
  FI: 'Europe/Helsinki',
  PL: 'Europe/Warsaw',
  CZ: 'Europe/Prague',
  SK: 'Europe/Bratislava',
  HU: 'Europe/Budapest',
  SI: 'Europe/Ljubljana',
  HR: 'Europe/Zagreb',
  GR: 'Europe/Athens',
  US: 'America/New_York',
};

export const getTimezoneForCountry = (countryCode: string | null | undefined): string => {
  if (!countryCode) return 'Europe/Vienna';
  return countryToTimezone[countryCode.toUpperCase()] || 'Europe/Vienna';
};

/** Intl locale for PDF/offer number, date, and % formatting (matches `OfferTemplateInput.language`, e.g. `EN` → en-GB). */
export const intlLocaleForOfferLanguage = (language: string | null | undefined): string => {
  const l = `${language || ''}`.trim().toLowerCase();
  if (l === 'en' || l.startsWith('en-') || l.startsWith('en_')) return 'en-GB';
  return 'de-AT';
};

export const formatOfferDay = (day: number, t?: (key: string, options?: any) => string) => {
  if (day < 0) {
    return t ? t('common:checkout.pre_day') : 'Vortag';
  }
  return t ? t('common:day_number', { number: day + 1 }) : 'Tag ' + (day + 1);
};

export const formatPrice = (price: number, language: string = 'de-AT', currency: string = 'EUR') => {
  if (isNaN(price)) return 'NaN';
  if (price === NO_PRICE) return '';

  const format = new Intl.NumberFormat(language, {
    style: 'currency',
    currency: currency,
  });
  return format.format(price);
};

export const formatNumber = (value: number, language: string = 'de-DE', precision: number = 0): string => {
  if (isNaN(value)) return 'NaN';
  return new Intl.NumberFormat(language, { minimumFractionDigits: precision, maximumFractionDigits: precision }).format(value);
};
export const formatPercentage = (percentage: number, language: string = 'de-AT', precision: number = 2) => {
  if (isNaN(percentage)) return 'NaN';

  const format = new Intl.NumberFormat(language, {
    style: 'percent',
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });
  return format.format(percentage);
};
export const formatDate = (date: Date, language: string = 'de-AT', timeZone: string = 'Europe/Vienna') => {
  if (isNaN(date.getTime())) return 'NaD';

  const format = new Intl.DateTimeFormat(language, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone,
  });
  return format.format(date);
};
export const formatDateTime = (date: Date, language: string = 'de-AT', timeZone: string = 'Europe/Vienna') => {
  if (isNaN(date.getTime())) return 'NaD';

  const format = new Intl.DateTimeFormat(language, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone,
  });
  return format.format(date);
};
export const formatTime = (date: Date, language: string = 'de-AT', timeZone: string = 'Europe/Vienna') => {
  if (isNaN(date.getTime())) return 'NaD';

  const format = new Intl.DateTimeFormat(language, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  });
  return format.format(date);
};

export const formatOccupancySeating = (seating: string, language: string = 'de-AT') => {
  const english = (language || '').toLowerCase().startsWith('en');
  if (seating === 'UFORM') return english ? 'U-Shape' : 'U-Form';
  if (seating === 'BLOCK') return 'Block';
  if (seating === 'THEATER') return 'Theater';
  if (seating === 'PARLIAMENT' || seating === 'PARLAMENT') return english ? 'Parliament' : 'Parlament';
  if (seating === 'BANKETT') return english ? 'Banquet' : 'Bankett';
  if (seating === 'CIRCLE') return english ? 'Circle' : 'Kreis';
  if (seating === 'COCKTAIL') return 'Cocktail';
  if (seating === 'OTHER') return english ? 'Other' : 'Sonstige';
  return '';
};

export const formatOccupancy = (occupancy: string, language: string = 'de-AT') => {
  if (occupancy === 'MORNING') return 'Vormittag';
  if (occupancy === 'AFTERNOON') return 'Nachmittag';
  if (occupancy === 'FULLDAY') return 'Ganztägig';
  return '';
};

const INDIVIDUAL_SEATING_DETAIL_MARKERS = ['Individuelle Bestuhlung: ', 'Individual seating: '] as const;

/**
 * Label prefixed to a booker's free-text seating request inside seminar-room line details.
 * @param language - Offer language. English starts with "en".
 */
export const individualSeatingDetailLabel = (language: string | null | undefined): string => {
  const english = `${language || ''}`.toLowerCase().startsWith('en');
  return english ? 'Individual seating: ' : 'Individuelle Bestuhlung: ';
};

/**
 * Separates the short seminar-room summary from an attached individual seating request.
 * @param details - Stored line-item description.
 */
export const splitIndividualSeatingDetails = (
  details: string | null | undefined,
): { summary: string; seatingText: string | null } => {
  const text = details || '';
  let markerAt = -1;
  let markerLength = 0;
  for (const marker of INDIVIDUAL_SEATING_DETAIL_MARKERS) {
    const index = text.indexOf(marker);
    if (index >= 0 && (markerAt < 0 || index < markerAt)) {
      markerAt = index;
      markerLength = marker.length;
    }
  }
  if (markerAt < 0) return { summary: text, seatingText: null };
  const seatingText = text.slice(markerAt + markerLength).trim();
  return {
    summary: text.slice(0, markerAt).replace(/\s+$/, ''),
    seatingText: seatingText || null,
  };
};

const STORED_SEATING_LABELS: Array<[string, string]> = [
  ['Sonstige', 'Other'],
  ['U-Form', 'U-Shape'],
  ['Parlament', 'Parliament'],
  ['Bankett', 'Banquet'],
  ['Kreis', 'Circle'],
];

const STORED_ROOM_EQUIPMENT: [string, string] = [
  'Inkludiert: 1x Beamer & Leinwand / Flatscreen, 1x Flipchart, 1x Pinnwand',
  'Included: 1x Projector & Screen / Flatscreen, 1x Flipchart, 1x Pinboard',
];

/**
 * Rewrites a stored seminar-room description into the language the booker is viewing.
 * The free-text request itself is left unchanged.
 * @param details - Stored line-item description.
 * @param language - Active UI language. English starts with "en".
 */
export const localizeStoredSeminarDetails = (details: string | null | undefined, language: string | null | undefined): string => {
  const english = `${language || ''}`.toLowerCase().startsWith('en');
  const { summary, seatingText } = splitIndividualSeatingDetails(details);
  let next = summary;

  for (const [german, englishLabel] of STORED_SEATING_LABELS) {
    const from = english ? german : englishLabel;
    const to = english ? englishLabel : german;
    if (next.startsWith(`${from},`) || next.startsWith(`${from} `)) {
      next = `${to}${next.slice(from.length)}`;
      break;
    }
  }

  next = english ? next.replace(/\bTeilnehmer\b/g, 'participants') : next.replace(/\bparticipants\b/gi, 'Teilnehmer');
  const [equipmentDe, equipmentEn] = STORED_ROOM_EQUIPMENT;
  if (english && next.includes(equipmentDe)) next = next.split(equipmentDe).join(equipmentEn);
  if (!english && next.includes(equipmentEn)) next = next.split(equipmentEn).join(equipmentDe);

  if (!seatingText) return next;
  const label = individualSeatingDetailLabel(language);
  return next ? `${next}\n\n${label}${seatingText}` : `${label}${seatingText}`;
};

/**
 * Shows the seminar-room line title in the language the booker is viewing.
 * @param header - Stored line-item title.
 * @param language - Active UI language. English starts with "en".
 */
export const localizeSeminarRoomHeader = (header: string | null | undefined, language: string | null | undefined): string => {
  if (header !== 'Seminarraum' && header !== 'Seminar Room') return header || '';
  return `${language || ''}`.toLowerCase().startsWith('en') ? 'Seminar Room' : 'Seminarraum';
};

export const slugify = (text: string) => {
  return text
    .toString()
    .toLowerCase()
    .replace(/_/g, '-') // Replace underscores with -
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/[^\w-]+/g, '') // Remove all non-word chars
    .replace(/--+/g, '-') // Replace multiple - with single -
    .replace(/^-+/, '') // Trim - from start of text
    .replace(/-+$/, ''); // Trim - from end of text
};
